import { DEFAULT_BRAND } from "../constants/brandDefaults";
import { corsSafeLogoUrl, isOvhObjectStorageUrl, resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { fetchProxiedRemoteBlobUrl } from "../utils/videoFrameCapture";

let lastBrandFingerprint = "";

const PWA_ID_VERSION = "v5";

/**
 * Display name for installed PWA / install CTAs.
 */
export function resolvePwaBrandName(brand) {
  const b = brand || {};
  return (
    String(b.brand_name || b.club_name || "").trim() ||
    DEFAULT_BRAND.brand_name ||
    "SmartTT Club"
  );
}

export function resolvePwaShortName(brand) {
  const b = brand || {};
  const short = String(b.brand_short_name || "").trim();
  if (short) return truncateShortName(short, 12);
  return truncateShortName(resolvePwaBrandName(b), 12);
}

function truncateShortName(name, max = 12) {
  const s = String(name || "").trim();
  if (!s) return "SmartTT";
  if (s.length <= max) return s;
  return s.slice(0, max).trim();
}

function setMetaContent(selector, content) {
  const el = document.querySelector(selector);
  if (el && content) el.setAttribute("content", content);
}

/**
 * Club logo for PWA icons only — never favicon/appicon (SmartTT shield).
 */
export function resolveClubLogoUrlForPwa(brand) {
  const b = brand || {};
  return (
    resolveClubImageUrl(b.logo_url) ||
    resolveClubImageUrl(b.logo_light_url) ||
    null
  );
}

function clubKeyForPwa(brand) {
  const id = brand && brand.club_id != null ? String(brand.club_id) : "platform";
  return id.trim().replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 80) || "platform";
}

function brandFingerprint(brand, name, shortName, theme, background) {
  const logo = resolveClubLogoUrlForPwa(brand) || "";
  return [name, shortName, theme, background, logo].join("|");
}

function isCrossOriginHttpUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (!/^https?:\/\//i.test(url)) return false;
  try {
    return new URL(url).origin !== window.location.origin;
  } catch {
    return true;
  }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // OVH sends no CORS headers. Anonymous mode fails the request and can
    // poison later no-cors loads of the same club logo.
    if (isCrossOriginHttpUrl(url) && !isOvhObjectStorageUrl(url)) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load icon: ${url}`));
    img.src = url;
  });
}

async function resolveCanvasSafeLogoUrl(sourceUrl) {
  if (!sourceUrl) return { url: null, revoke: null };
  if (!isCrossOriginHttpUrl(sourceUrl)) {
    return { url: sourceUrl, revoke: null };
  }

  const sameOriginProxy = `${window.location.origin}/pwa-logo-proxy?url=${encodeURIComponent(sourceUrl)}`;
  try {
    await loadImage(sameOriginProxy);
    return { url: sameOriginProxy, revoke: null };
  } catch {
    /* try authenticated API proxy */
  }

  try {
    const objectUrl = await fetchProxiedRemoteBlobUrl(sourceUrl);
    if (objectUrl) return { url: objectUrl, revoke: objectUrl };
  } catch {
    /* fall through */
  }

  if (isOvhObjectStorageUrl(sourceUrl)) {
    const viaApi = corsSafeLogoUrl(sourceUrl);
    if (viaApi && viaApi !== sourceUrl) {
      try {
        await loadImage(viaApi);
        return { url: viaApi, revoke: null };
      } catch {
        /* public proxy unavailable */
      }
    }
    return { url: null, revoke: null };
  }

  return { url: sourceUrl, revoke: null };
}

/**
 * Draw a square PNG (exact size) suitable for Chrome install icons.
 */
export async function rasterizeSquareIcon(sourceUrl, size, background) {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unsupported");

  ctx.fillStyle = background || "#ffffff";
  ctx.fillRect(0, 0, size, size);

  let revoke = null;
  if (sourceUrl) {
    try {
      const safe = await resolveCanvasSafeLogoUrl(sourceUrl);
      revoke = safe.revoke;
      if (!safe.url) throw new Error("logo not canvas-safe");
      const img = await loadImage(safe.url);
      const pad = Math.round(size * 0.08);
      const box = size - pad * 2;
      const scale = Math.min(box / img.width, box / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (size - w) / 2;
      const y = (size - h) / 2;
      ctx.drawImage(img, x, y, w, h);
    } catch {
      throw new Error("logo rasterize failed");
    } finally {
      if (revoke) {
        try {
          URL.revokeObjectURL(revoke);
        } catch {
          /* ignore */
        }
      }
    }
  }

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob failed"))),
      "image/png"
    );
  });
  return blob;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

/**
 * Upload rasterized icons to the OVH/webpack server so Chrome’s install
 * fetcher (which bypasses the service worker) receives the club logo.
 */
async function uploadClubIconsToServer(clubKey, blob192, blob512) {
  const [icon192, icon512] = await Promise.all([
    blobToBase64(blob192),
    blobToBase64(blob512),
  ]);
  const res = await fetch(
    `${window.location.origin}/pwa-icons/${encodeURIComponent(clubKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ icon192, icon512 }),
    }
  );
  if (!res.ok && res.status !== 204) {
    throw new Error(`icon upload failed: ${res.status}`);
  }
}

async function cachePwaIcons(brand) {
  const logoUrl = resolveClubLogoUrlForPwa(brand);
  const clubKey = clubKeyForPwa(brand);
  const background =
    (brand && (brand.color_surface || brand.color_background)) || "#ffffff";

  if (!logoUrl) {
    return { ok: false, clubKey };
  }

  const [blob192, blob512] = await Promise.all([
    rasterizeSquareIcon(logoUrl, 192, background),
    rasterizeSquareIcon(logoUrl, 512, background),
  ]);

  let uploaded = false;
  try {
    await uploadClubIconsToServer(clubKey, blob192, blob512);
    uploaded = true;
  } catch {
    /* HTTP fallback icons still work */
  }

  return { ok: true, clubKey, uploaded };
}

/** Origin, or the GCS bucket URL when the app is hosted under a path. */
function hostedAppRoot() {
  const pub = String(window.__PUBLIC_URL__ || "").replace(/\/$/, "");
  if (pub) return pub;
  const base = String(window.__ROUTER_BASENAME__ || "").replace(/\/$/, "");
  if (base) {
    const path = base.startsWith("/") ? base : `/${base}`;
    return `${window.location.origin}${path}`;
  }
  return window.location.origin;
}

function pwaStartUrl() {
  return `${hostedAppRoot()}/login`;
}

function pointManifestLinkToSameOrigin(fingerprint) {
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  const stable = encodeURIComponent(fingerprint || "default").slice(0, 80);
  const next = `${hostedAppRoot()}/manifest.json?b=${stable}`;
  if (link.getAttribute("href") === next) return;
  link.href = next;
}

async function postBrandToServiceWorker(brandPayload) {
  if (!("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  const target = reg?.active || navigator.serviceWorker.controller;
  if (!target) return;
  target.postMessage({ type: "SET_PWA_BRAND", brand: brandPayload });
}

function onControllerChangeRepost(brandPayload) {
  if (!("serviceWorker" in navigator)) return;
  const handler = () => {
    postBrandToServiceWorker(brandPayload).catch(() => undefined);
  };
  navigator.serviceWorker.addEventListener("controllerchange", handler, {
    once: true,
  });
}

/**
 * Apply club name/logo to the installable PWA (same-origin manifest via SW).
 * start_url is always /login on the current host (OVH or GCP).
 */
export async function applyBrandWebAppManifest(brand) {
  if (typeof document === "undefined" || typeof window === "undefined") return;

  const name = resolvePwaBrandName(brand);
  const shortName = resolvePwaShortName(brand);
  const theme =
    (brand && brand.color_primary) || DEFAULT_BRAND.color_primary || "#0877BC";
  const background =
    (brand && (brand.color_surface || brand.color_background)) || "#ffffff";
  const description =
    (brand && brand.meta_description) ||
    DEFAULT_BRAND.meta_description ||
    `${name} — SmartTT Club training app.`;

  const fingerprint = brandFingerprint(
    brand,
    name,
    shortName,
    theme,
    background
  );
  const brandChanged = fingerprint !== lastBrandFingerprint;

  setMetaContent('meta[name="apple-mobile-web-app-title"]', shortName);
  setMetaContent('meta[name="application-name"]', shortName);

  let iconResult = { ok: false, clubKey: clubKeyForPwa(brand) };
  try {
    iconResult = await cachePwaIcons(brand);
  } catch {
    /* static /pwa-icon-*.png still used */
  }

  const clubKey = iconResult.clubKey || clubKeyForPwa(brand);
  const appRoot = hostedAppRoot();
  const icon192Url = `${appRoot}/pwa-icons/${encodeURIComponent(clubKey)}/icon-192.png`;
  const icon512Url = `${appRoot}/pwa-icons/${encodeURIComponent(clubKey)}/icon-512.png`;

  if (iconResult.ok) {
    const apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (apple) apple.href = `${icon192Url}?t=${Date.now()}`;
    const icon = document.querySelector('link[rel="icon"]');
    if (icon) icon.href = `${icon192Url}?t=${Date.now()}`;
  }

  const payload = {
    name,
    short_name: shortName,
    description,
    theme_color: theme,
    background_color: background,
    start_url: pwaStartUrl(),
    club_id: clubKey,
    id: `${appRoot}/pwa/${PWA_ID_VERSION}/${encodeURIComponent(clubKey)}`,
    icon_192: iconResult.uploaded
      ? icon192Url
      : `${appRoot}/pwa-icon-192.png`,
    icon_512: iconResult.uploaded
      ? icon512Url
      : `${appRoot}/pwa-icon-512.png`,
  };

  await postBrandToServiceWorker(payload);
  onControllerChangeRepost(payload);

  if (brandChanged) {
    lastBrandFingerprint = fingerprint;
    pointManifestLinkToSameOrigin(fingerprint);
  } else {
    pointManifestLinkToSameOrigin(lastBrandFingerprint || fingerprint);
  }
}
